/**
 * 案件起票モーダルのプレゼンテーション部品（ステップ表示・ピルラベル・chip ドロップダウン等）。
 * ProjectIntakeModal から分離した表示専用コンポーネント群。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票フォームのラベルピル・chip セレクト・憲章入力欄などの表示部品
 * @graph-connects getBusinessTheme [calls] 事業テーマ色 SSOT を取得
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { getBusinessTheme } from '../../../lib/performance-agg-ui.js';

// DEV プレビュー用のモック選択肢は dev-mock-options.ts に分離し、ProjectIntakeModal の
// import.meta.env.DEV 分岐からのみ参照する。表示専用の本モジュールからは公開しない。

/** 事業プルダウンの表示順（この順に並べ、リスト外は元順で末尾）。 @graph-connects none */
export const BUSINESS_ORDER = [
  'エアクロ',
  'エアクロモール',
  'エアクロメンズ',
  'Spot Rental',
  'AC PORT',
  '全社インフラ',
  '社長室',
  '経営管理室',
  'Other',
] as const;

/** 事業「エアクロ」選択時に担当チームの先頭へ寄せる順序。 @graph-connects none */
export const AIRCLOSET_TEAM_TOP = [
  'CDT',
  'BDT',
  'CRMT',
  'QDT',
  'NBT',
  'SXT',
  'FCT',
  'PST',
  'CLT',
  'STYT', // 2026-08 改組で CXG に新設（新設当初の名称は STT）
  'CST',
] as const;

/**
 * order に沿って安定ソートする（order に無い値は元の順序を保って末尾へ）。
 * @graph-connects none
 */
export function orderByList<T>(
  items: T[],
  order: readonly string[],
  key: (item: T) => string,
): T[] {
  const rank = (value: string): number => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };
  return [...items].sort((a, b) => rank(key(a)) - rank(key(b)));
}

/**
 * ステップインジケータ（Step 1 → Step 2 → 完了）。到達済みのステップを天色（sky）で示す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起票モーダルの進行状況を 3 ノード（丸 + 接続線）で表示する
 * @graph-connects none
 */
export function StepIndicator({ current }: { current: 1 | 2 | 3 }): ReactNode {
  const steps = [
    { n: 1 as const, label: 'Step 1' },
    { n: 2 as const, label: 'Step 2' },
    { n: 3 as const, label: '完了' },
  ];
  return (
    <div className="flex items-center justify-center px-2 py-1">
      {steps.map((s, i) => {
        const reached = current >= s.n;
        return (
          <Fragment key={s.n}>
            <div className="flex flex-col items-center">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  reached
                    ? 'border-sky-400 bg-sky-400 text-white'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
                }`}
              >
                {s.n === 3 ? '✓' : s.n}
              </div>
              <span
                className={`mt-1 text-xs font-medium ${
                  reached ? 'text-sky-500' : 'text-[var(--color-text-muted)]'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`mx-2 mb-5 h-0.5 w-16 transition-colors sm:w-24 ${
                  current > s.n ? 'bg-sky-400' : 'bg-[var(--color-border)]'
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * 事業 → ピル配色。performance-agg-ui の事業テーマ色 SSOT を流用する
 * （エアクロ=aC Div 赤 #DC4040 / エアクロモール=Mall 緑 / AC PORT=Logi 青 …、未知は灰）。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 事業名を主題色ピルの配色（背景色・文字色）へ変換する
 * @graph-connects getBusinessTheme [calls] 事業テーマ色 SSOT を取得
 */
export function businessTone(value: string): { bg: string; text: string } {
  const theme = getBusinessTheme(value);
  return { bg: theme.color, text: theme.textColor };
}

/**
 * フィールドラベルのピル（カプセル）チップ。tone 指定で任意色、emphasis で赤（必須強調）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business フォーム項目名をカプセル型チップで表示する（色指定・強調対応）
 * @graph-connects none
 */
function FieldPill({
  children,
  tone,
  emphasis = false,
}: {
  children: ReactNode;
  tone?: { bg: string; text: string };
  emphasis?: boolean;
}): ReactNode {
  const style = tone ? { backgroundColor: tone.bg, color: tone.text } : undefined;
  const cls = tone
    ? ''
    : emphasis
      ? 'bg-[var(--color-error)] text-white'
      : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]';
  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${cls}`}
      style={style}
    >
      {children}
    </span>
  );
}

/**
 * ピルラベル付きの汎用フィールド枠（ラベルチップ + 任意のコントロール）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ピルラベルと入力コントロールを縦に並べる 1 フィールド枠
 * @graph-connects none
 */
export function Field({
  label,
  tone,
  emphasis = false,
  className,
  children,
}: {
  label: string;
  tone?: { bg: string; text: string };
  emphasis?: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  // 必須マーク（末尾の「*」）は赤字で示す。tone / emphasis 指定の色付きピルでは
  // 背景色と干渉するため、既定ピル（色指定なし）のときのみ赤字化する。
  const requiredMark = !tone && !emphasis && label.endsWith('*');
  const labelBody = requiredMark ? label.replace(/\s*\*$/, '') : label;
  return (
    <div className={className ? `space-y-1.5 ${className}` : 'space-y-1.5'}>
      <FieldPill tone={tone} emphasis={emphasis}>
        {labelBody}
        {requiredMark && <span className="ml-1 text-[var(--color-error)]">*</span>}
      </FieldPill>
      {children}
    </div>
  );
}

/**
 * chip の配色パレット（チーム・メンバー用）。value のハッシュで安定的に 1 色を割り当てる。
 * @graph-connects none
 */
const CHIP_PALETTE: readonly { bg: string; text: string }[] = [
  { bg: '#EF4444', text: '#FFFFFF' },
  { bg: '#F97316', text: '#FFFFFF' },
  { bg: '#EAB308', text: '#422006' },
  { bg: '#22C55E', text: '#052E16' },
  { bg: '#14B8A6', text: '#04312C' },
  { bg: '#3B82F6', text: '#FFFFFF' },
  { bg: '#6366F1', text: '#FFFFFF' },
  { bg: '#A855F7', text: '#FFFFFF' },
  { bg: '#EC4899', text: '#FFFFFF' },
  { bg: '#64748B', text: '#FFFFFF' },
];

/**
 * 文字列を安定色（CHIP_PALETTE の 1 色）へ写像する。チーム名・nickname のチップ色に使う。
 * @graph-connects none
 */
function hashTone(value: string): { bg: string; text: string } {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) {
    h = (h * 31 + value.charCodeAt(i)) >>> 0;
  }
  return CHIP_PALETTE[h % CHIP_PALETTE.length];
}

/**
 * チーム固有色（team-badge.ts の AC_TEAM_COLORS / Mall 緑 / Logi 青 と一致）。
 * @graph-connects none
 */
const TEAM_COLORS: Readonly<Record<string, { bg: string; text: string }>> = {
  CDT: { bg: '#0277BD', text: '#FFFFFF' },
  CRMT: { bg: '#EC4899', text: '#FFFFFF' }, // FCT へ統合された旧チーム。過去 team_name 用に残置
  BDT: { bg: '#EAB308', text: '#422006' },
  QDT: { bg: '#8BA63F', text: '#FFFFFF' },
  NBT: { bg: '#4B5563', text: '#FFFFFF' },
  // 2026-08 の aC Div 改組で追加された 3 チーム。team-badge.ts の AC_TEAM_COLORS と
  // 同値を維持する（未定義だと teamTone が hashTone にフォールバックし、起票 chip が
  // ブランド色と不一致・他チームと撞色する）。
  FCT: { bg: '#EC4899', text: '#FFFFFF' }, // CRMT 統合チームのため旧 CRMT の粉色を継承
  CLT: { bg: '#0D9488', text: '#FFFFFF' },
  STYT: { bg: '#64748B', text: '#FFFFFF' },
};

/**
 * チーム名 → チップ配色。既定義チームは team-badge 準拠、未定義は hashTone で自動採色する。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business チーム名をチップの配色へ変換する（既定義色または自動採色）
 * @graph-connects none
 */
export function teamTone(team: string): { bg: string; text: string } {
  const known = TEAM_COLORS[team];
  if (known) return known;
  if (team.startsWith('M-')) return { bg: '#50BA52', text: '#FFFFFF' }; // Mall Div 緑
  if (team === 'LPT' || team === 'SLT') return { bg: '#56C3DA', text: '#FFFFFF' }; // Logi 青
  return hashTone(team);
}

/**
 * chip ドロップダウン（Google Sheets 風）。選択値を tone 色のカプセルで表示し、候補も色付きで並べる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 単一選択のドロップダウン。選択中/候補の値を色付きカプセル（chip）で表示する
 * @graph-connects none
 */
export function ChipSelect({
  value,
  options,
  onChange,
  placeholder,
  toneFor,
  error,
  disabled = false,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder: string;
  toneFor: (value: string) => { bg: string; text: string };
  error?: string;
  disabled?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex h-[42px] w-full items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 text-sm disabled:opacity-50"
      >
        {value ? (
          <FieldPill tone={toneFor(value)}>{value}</FieldPill>
        ) : (
          <span className="text-[var(--color-text-muted)]">{placeholder}</span>
        )}
        <span className="ml-2 text-[var(--color-text-muted)]" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-1 shadow-lg"
        >
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className="flex w-full items-center rounded px-2 py-1.5 text-left hover:bg-[var(--color-bg-hover)]"
            >
              <FieldPill tone={toneFor(o.value)}>{o.label}</FieldPill>
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-[var(--color-error)]">{error}</p>}
    </div>
  );
}

/**
 * Step 2 の憲章テキストエリア 1 項目。ラベルはピル、emphasis=true で赤ピル。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト憲章の 1 入力項目（ピルラベル + テキストエリア）を描画する
 * @graph-connects none
 */
export function CharterField({
  id,
  label,
  value,
  emphasis = false,
  readOnly = false,
  maxLength,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  emphasis?: boolean;
  readOnly?: boolean;
  /** 入力上限文字数（サーバー側 schema の .max() と揃える） */
  maxLength?: number;
  onChange?: (v: string) => void;
}): ReactNode {
  return (
    <Field label={label} emphasis={emphasis}>
      <Textarea
        id={id}
        rows={readOnly ? 1 : 2}
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        maxLength={maxLength}
        onChange={
          onChange
            ? (e) => {
                onChange(e.target.value);
              }
            : undefined
        }
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
      />
    </Field>
  );
}
